/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useEffect,
  useRef,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useReducer,
  useContext,
} from 'react';
import { useLogtoAdapter, type LogtoSignInOptions } from '@lib/logto';
import { signedInCueSlot } from '@lib/magicLink/signedInCue';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useStableNavigate } from '@hooks/useStableRouter';
import { useAuthStore } from '@stores/authStore';
import { setGlobalAuthTokenGetter, setGlobalSessionExpired } from '@api-client/api-client';
import { storeRedirectPath } from '@utils/auth';
import { CrossTabAuthSync } from '@utils/crossTabAuthSync';
import {
  signedOutByOtherTabSlot,
  userInitiatedSignInSlot,
} from '@utils/crossTabAuthSync/authFlowFlags';
import { useSlotAtMountAndClear } from '@hooks/useSlotAtMountAndClear';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { useOneSignalIdentity } from '@hooks/useOneSignalIdentity';
import { useTakeoverFeed } from '@hooks/useTakeoverFeed';
import FunnyLoadingMessage from '@components/FunnyLoadingMessage';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import { clearIntents } from '@lib/overlay/intents';
import { isNativePlatform } from '@lib/platform';
import { logger } from '@utils/logger';
import {
  clearPendingQuestionClaimInTab,
  deferUnnamedPendingQuestionClaimInTab,
} from '@utils/pendingQuestionClaim';
import { SessionExpiredDrawer } from '@components/SessionExpiredDrawer';
import {
  clearRecoveryRecord,
  recordTokenRefreshFailure,
  resetTokenRefreshFailures,
} from '@utils/authRecovery';

// Import from auth module
import {
  AuthSessionState,
  AuthContextType,
  authReducer,
  initialAuthState,
  createSessionStateCheckers,
  isInvalidGrantError,
  isNotAuthenticatedError,
  useInvalidGrantHandler,
  useProactiveTokenRefresh,
  useSdkLoadingWatchdog,
  useCrossTabAuthSync,
  useAuthNavigation,
  isPublicRoute,
  useAuthAnalytics,
  useSignInOut,
  transitionToGuestSession,
  clearAuthState,
  LogoutCancelledError,
  shouldFireRecovery,
} from './auth';

// =============================================================================
// DRAWER STATE
// =============================================================================

/**
 * Single source of truth for the SessionExpiredDrawer's visibility AND variant.
 *
 * Issue: prior to this refactor the drawer was driven by two independent
 * useState hooks (`isSessionExpiredDrawerOpen` + `drawerVariant`) that were
 * decoupled from `sessionState`. That allowed the drawer to stay open after
 * the auth state machine recovered (false-positive opens), and forced every
 * close path to manually clean up multiple gates.
 *
 * Fix: collapse to a single discriminated string. `'closed'` is the only
 * dismissed state. `'session-expired'` and `'service-unavailable'` map 1:1
 * to the drawer's variants. The render-time projection `effectiveDrawerState`
 * (defined in the component body) hides the `'session-expired'` intent
 * whenever `sessionState === AUTHENTICATED`, so a transient SDK blip that
 * resolves before the drawer opens can't leave it stuck visible. The
 * projection is render-derived rather than effect-driven so the auth state
 * machine and drawer visibility commit together in one frame, eliminating
 * the open→effect→close flicker the previous architecture was prone to.
 */
type SessionDrawerState = 'closed' | 'session-expired' | 'service-unavailable';

function assertNever(value: never): never {
  throw new Error(`Unexpected SessionDrawerState: ${String(value)}`);
}

/**
 * Maps the discriminated drawer state to the underlying `<SessionExpiredDrawer />`
 * `variant` prop. Exhaustive switch + `assertNever` so a future variant added
 * to `SessionDrawerState` produces a compile-time error here instead of
 * silently routing to `'session-expired'`.
 */
function drawerVariantFor(state: SessionDrawerState): 'session-expired' | 'service-unavailable' {
  switch (state) {
    case 'service-unavailable':
      return 'service-unavailable';
    case 'session-expired':
      return 'session-expired';
    case 'closed':
      // Unreachable in practice — the drawer is rendered with
      // `open={state !== 'closed'}`, so the variant prop is ignored when
      // closed. Return the default to satisfy the function's type.
      return 'session-expired';
    default:
      return assertNever(state);
  }
}

/**
 * Tolerance window before treating an `AUTHENTICATED → !logtoIsAuthenticated`
 * flip as a real session loss. The Logto SDK can briefly report
 * `isAuthenticated=false` during a refresh round-trip, a cross-tab broadcast,
 * or a WebView focus event. Reacting immediately would clear real tokens for
 * what was actually a transient blip.
 *
 * 1500ms is long enough for typical SDK retries to settle but short enough
 * that genuine expirations still feel responsive to the user.
 */
const UNEXPECTED_LOSS_TOLERANCE_MS = 1500;

// Re-export types for consumers
export { AuthSessionState } from './auth';
export type { AuthContextType } from './auth';

// =============================================================================
// HELPERS
// =============================================================================

const parseLogtoResources = (rawResources?: string): string[] => {
  if (!rawResources) {
    return [];
  }

  return rawResources
    .split(',')
    .map((entry: string) => entry.trim())
    .filter((entry: string) => entry.length > 0);
};

// =============================================================================
// CONTEXT
// =============================================================================

/**
 * Authentication Context
 * Exported for useAuth hook in hooks/useAuth.ts
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// PROVIDER PROPS
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

// =============================================================================
// AUTH PROVIDER
// =============================================================================

/**
 * Centralized Authentication Provider
 *
 * This component orchestrates ALL authentication logic by composing focused hooks:
 * - useInvalidGrantHandler: Handles session expiry and silent re-auth
 * - useProactiveTokenRefresh: Keeps tokens fresh before expiry
 * - useCrossTabAuthSync: Coordinates auth across browser tabs
 * - useAuthNavigation: Protects routes and handles redirects
 * - useAuthAnalytics: Tracks auth events and user properties
 * - useSignInOut: Provides sign-in/out actions
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  // Stable on purpose. This is AuthProvider's ONLY router read, and it feeds exactly one call (the
  // `/home` bump in the session-expired drawer handler below), so nothing here renders from the
  // URL. `useNavigate()` would still subscribe to RouteContext, whose identity react-router rebuilds
  // on every navigation, and an AuthProvider re-render is not cheap: it re-runs every auth hook and
  // re-evaluates the 14-dependency AuthContext memo that every useAuth() consumer in the app reads.
  // A single churning dependency there re-renders all of them past their memo() boundaries, so this
  // one line is what keeps a `?tab=` replace from reaching them at all. See stableRouterContext.ts.
  const navigate = useStableNavigate();
  const {
    trackEvent,
    setUserProperties,
    setUserId,
    trackSessionStarted,
    checkRetentionMilestones,
  } = useAmplitudeTracking();

  // ─────────────────────────────────────────────────────────────────────────────
  // Draft Dialog State (UI stays in provider)
  // ─────────────────────────────────────────────────────────────────────────────
  const [isDraftDialogOpen, setIsDraftDialogOpen] = useState(false);
  const dialogCloseIntentRef = useRef<'confirm' | 'cancel' | null>(null);
  const pendingSignOutPromiseRef = useRef<Promise<void> | null>(null);
  const signOutResolverRef = useRef<(() => void) | null>(null);
  const signOutRejectRef = useRef<((reason?: unknown) => void) | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Expired Drawer State
  //
  // Single discriminated state — `'closed'` means hidden, the other two map
  // to the drawer's variants. Replaces a previously-decoupled pair of useState
  // hooks (open + variant) that allowed the drawer to outlive recovery.
  // ─────────────────────────────────────────────────────────────────────────────
  const [sessionDrawerState, setSessionDrawerState] = useState<SessionDrawerState>('closed');

  // Live ref mirror of `sessionDrawerState` so the drawer-open helpers can
  // make idempotency decisions synchronously — `useState` updates are queued
  // for the next render, which means StrictMode's double-effect invocation
  // and any future same-tick double-fire would both read 'closed' and pass
  // the guard.
  //
  // The mirror is maintained on TWO axes:
  //   1. Same-tick: `commitDrawerState` writes the ref synchronously, before
  //      queuing the React dispatch. A second helper call in the same tick
  //      sees the new value immediately.
  //   2. Cross-render: a `useEffect` on `[sessionDrawerState]` re-syncs the
  //      ref after every commit. Render-phase mutation (`ref.current = state`
  //      at the top of the component) was the obvious shape but breaks under
  //      React's concurrent rendering — an aborted render can leave the ref
  //      with a value that was never committed. The post-commit effect runs
  //      only on committed renders, keeping the ref aligned with state that
  //      actually shipped to React's tree. The funnel makes (2) defensive
  //      rather than load-bearing — every mutation already goes through
  //      `commitDrawerState` — but it's the safety net that catches a future
  //      direct `setSessionDrawerState` call from drifting the ref.
  const sessionDrawerStateRef = useRef<SessionDrawerState>(sessionDrawerState);
  useEffect(() => {
    sessionDrawerStateRef.current = sessionDrawerState;
  }, [sessionDrawerState]);

  // Single setter that pairs the ref update with the React state dispatch.
  // ALL drawer-state mutations go through this — direct `setSessionDrawerState`
  // calls would let the ref drift out of sync until the cross-render effect
  // re-syncs it, breaking the synchronous idempotency guard the helpers
  // depend on for same-tick decisions. Mechanical enforcement (one funnel,
  // one invariant) is stronger than per-callsite discipline. Stable identity
  // (`useCallback([])`) so dependents that take it as a dep don't invalidate
  // per render.
  const commitDrawerState = useCallback((next: SessionDrawerState) => {
    sessionDrawerStateRef.current = next;
    setSessionDrawerState(next);
  }, []);

  // Read at render, spent after commit (rule: `authFlowFlags.ts`). Render matters: the drawer
  // seeds its `useState` from this context value in its first render. Native answers false
  // (the drawer is web-only), though the flag is still spent there.
  const signedOutByOtherTabAtMount =
    useSlotAtMountAndClear(signedOutByOtherTabSlot) === true && !isNativePlatform();

  // ─────────────────────────────────────────────────────────────────────────────
  // Token Request Deduplication
  // ─────────────────────────────────────────────────────────────────────────────
  const tokenRequestRef = useRef<Promise<string | null> | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Shared Token Ref (used by both cross-tab sync and proactive refresh)
  // ─────────────────────────────────────────────────────────────────────────────
  const lastTokenRef = useRef<string | null>(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // Session State (useReducer for predictable state transitions)
  // ─────────────────────────────────────────────────────────────────────────────
  const [authState, dispatch] = useReducer(authReducer, initialAuthState);
  const { sessionState } = authState;
  const sessionStateRef = useRef<AuthSessionState>(sessionState);
  sessionStateRef.current = sessionState;

  // ─────────────────────────────────────────────────────────────────────────────
  // Logto SDK
  // ─────────────────────────────────────────────────────────────────────────────
  const logto = useLogtoAdapter();

  const {
    isAuthenticated: logtoIsAuthenticated,
    isLoading: logtoIsLoading,
    error: logtoError = null,
    signIn: sdkSignIn,
    signOut: logtoSignOut,
    getAccessToken,
    clearAllTokens,
  } = logto;

  // Ref for Logto auth state (avoids stale closures)
  const logtoIsAuthenticatedRef = useRef(logtoIsAuthenticated);
  logtoIsAuthenticatedRef.current = logtoIsAuthenticated;

  /**
   * The ONE way into a Logto sign-in. All three hooks that start one --
   * useSignInOut (every CTA and the door's own exits), useAuthNavigation's
   * protected-route guard, and useInvalidGrantHandler's recovery attempts --
   * receive this instead of the SDK's `signIn`, so starting a sign-in ends any
   * "Signed in as ..." cue armed in THIS TAB for an earlier one. The cue is
   * per-tab (#1117), so a sign-in in another tab leaves it alone.
   *
   * AIDEV-NOTE [MEETBALL magic-link]: issues #1116 and #1118. The cue used to be
   * cleared only by the door, and every sign-in that never mounts it leaked a
   * stale cue into `/callback`: the silent `prompt=none` re-auth and its
   * interactive fallback (a toast over a background token refresh), and in a
   * build with `magicLinkAuth` off every CTA and the auto-guard. Clearing here
   * covers those and any path added later. `keepSignedInCue` is for the
   * redemptions the cue was armed for; see `SignInOptions.keepSignedInCue`.
   */
  const logtoSignIn = useCallback(
    (
      options: LogtoSignInOptions,
      { keepSignedInCue = false }: { keepSignedInCue?: boolean } = {},
    ) => {
      if (!keepSignedInCue) {
        signedInCueSlot.clear();
      }
      return sdkSignIn(options);
    },
    [sdkSignIn],
  );

  // Diagnostic refs — let stable callbacks log the latest state without
  // re-creating themselves on every render. Read by both the production
  // `auth_drawer_opened` Amplitude event (the triage signal that survives
  // production builds) and the dev-only `[drawer-open]` console warn
  // (`logger.warn` is gated to `import.meta.env.DEV`); refs ensure the
  // captured snapshot reflects live state, not a stale closure.
  const logtoIsLoadingRef = useRef(logtoIsLoading);
  logtoIsLoadingRef.current = logtoIsLoading;
  const logtoErrorRef = useRef<Error | null>(logtoError);
  logtoErrorRef.current = logtoError;

  // Session state checkers for callbacks
  const sessionCheckers = useMemo(
    () => createSessionStateCheckers(sessionStateRef, logtoIsAuthenticatedRef),
    [],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // API Resources
  // ─────────────────────────────────────────────────────────────────────────────
  const logtoResources = useMemo(
    () => parseLogtoResources(import.meta.env.VITE_LOGTO_RESOURCE),
    [],
  );
  const primaryApiResource = logtoResources[0];

  // ─────────────────────────────────────────────────────────────────────────────
  // User Data (React Query)
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    user,
    isLoading: userLoading,
    isAuthenticated: userAuthenticated,
    error: userError,
    refetchUser,
    clearUser,
  } = useCurrentUser(sessionState);

  // ─────────────────────────────────────────────────────────────────────────────
  // Zustand Store
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    setCurrentUser,
    setAuthenticated,
    setError,
    clearCachedAuth,
    cachedUserInfo,
    wasAuthenticated,
  } = useAuthStore();

  // Diagnostic ref for `wasAuthenticated` (declared here because the Zustand
  // destructure happens above; refs read live Zustand state inside stable
  // callbacks).
  const wasAuthenticatedRef = useRef(wasAuthenticated);
  wasAuthenticatedRef.current = wasAuthenticated;

  // Ref for `cachedUserInfo`. Read once inside the boot-cached-invalid
  // branch's analytics call. Putting it in the effect's dep array would
  // re-run the effect whenever the Zustand cache mutates (e.g., right
  // after `clearCachedAuth()` flips it to null), which is redundant.
  const cachedUserInfoRef = useRef(cachedUserInfo);
  cachedUserInfoRef.current = cachedUserInfo;

  // ─────────────────────────────────────────────────────────────────────────────
  // Cross-Tab Sync
  // ─────────────────────────────────────────────────────────────────────────────
  // Identity-stable for the same reason as `openDraftDialog` below: both are dependencies of
  // useCrossTabAuthSync's listener-registration effect, so an inline arrow tore down and
  // re-registered all three CrossTabAuthSync listeners on every AuthProvider render. Navigation is
  // no longer one of those renders (see `useStableNavigate` above), but plenty remain - a Logto
  // state change, a useCurrentUser settle, any of this component's own setStates - and each one
  // would re-register. Neither handler closes over anything, so `[]` is exact.
  const handleSignedInInOtherTab = useCallback(() => {
    // AIDEV-NOTE [MEETBALL magic-link]: #1279 D-08, a sign-in elsewhere defers this tab's flag.
    // This reload hands this tab a session with no `/callback` of its own, so a raw unnamed flag
    // left here would wait for this tab's next interactive `/callback` to spend it by device
    // token, under whichever account signs in there. This runs for every SIGNED_IN delivered to
    // this tab while it is running, continuation on or off: a same-browser `/auth/link` that
    // reserved the flow (`taken`) or failed open (`proceed`) and redeemed in its own tab, which no
    // door stand-down sees, and a sibling door, whose stand-downs defer first. A sign-in that is
    // not delivered leaves the flag raw, as before: a tab not running at the broadcast
    // (discarded, killed, restored) boots with the shared tokens; a redemption whose
    // `userInitiatedSignInSlot` is missing or more than 30 minutes old broadcasts nothing; and
    // without BroadcastChannel, the storage fallback reads a refresh token that is already present
    // as a rotation, not a sign-in. The export exempts native itself.
    deferUnnamedPendingQuestionClaimInTab();
    window.location.reload();
  }, []);

  const handleSignedOutInOtherTab = useCallback(() => {
    // This tab's queued overlays belong to the account that just signed out in
    // the other tab; sessionStorage survives the redirect below, so sweep the
    // intent channel (through the store, not just the raw key) before leaving.
    clearIntents();
    // ...and this tab's guest pending-question claim, for the same reason (#1279
    // review, S-02): the signing-out tab's sweep never reaches this tab's
    // sessionStorage, so a raw flag left here would be spent, and a deferred
    // marker offered, at the next sign-in in this tab, whoever's it is.
    clearPendingQuestionClaimInTab();
    window.location.href = '/home';
  }, []);

  const { getAccessTokenWithCrossTabLock } = useCrossTabAuthSync({
    getAccessToken,
    apiResource: primaryApiResource,
    logtoIsAuthenticatedRef,
    lastTokenRef,
    onSignedIn: handleSignedInInOtherTab,
    onSignedOut: handleSignedOutInOtherTab,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Invalid Grant Handler
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Caller identification for the diagnostic log emitted on every drawer open.
   * When users report a false-positive drawer in production, this is the only
   * signal that tells us which of the five entry paths fired.
   */
  type SessionExpiredReason =
    | 'invalid-grant-terminal' // useInvalidGrantHandler.goTerminal()
    | 'sdk-watchdog' // useSdkLoadingWatchdog 5s timeout
    | 'boot-cached-invalid' // boot-time INITIALIZING + wasAuthenticated branch
    | 'unexpected-loss'; // runtime AUTHENTICATED + !logtoIsAuthenticated branch

  const openSessionExpiredDrawer = useCallback(
    (reason: SessionExpiredReason) => {
      // Idempotency: each call site has its own state-machine guard
      // (handleInvalidGrant early-returns on REAUTHENTICATING/SESSION_EXPIRED,
      // useSdkLoadingWatchdog refuses to arm on those states, the boot-cached
      // branch only runs in INITIALIZING, the unexpected-loss timer requires
      // AUTHENTICATED). Those guards rely on per-path state reads that future
      // refactors could weaken. Capturing the invariant at the helper itself
      // is defense-in-depth — the side effects (Amplitude event,
      // storeRedirectPath, dev-log) must not duplicate when the drawer is
      // already requested in this variant. Sync write before the dispatch
      // closes the StrictMode double-effect window where the queued setState
      // hasn't committed yet.
      if (sessionDrawerStateRef.current === 'session-expired') {
        logger.debug('[drawer-open] suppressed duplicate session-expired open', { reason });
        return;
      }
      commitDrawerState('session-expired');

      // Production telemetry: every drawer-open commit is tracked so a future
      // false-positive can be triaged from Amplitude. Mirrors the
      // `auth_cached_session_invalid` precedent in the boot path. The dev-only
      // `logger.warn` below stays for local console reproduction; in
      // production the Amplitude event is the only signal that survives.
      trackEvent(ANALYTICS_EVENTS.auth_drawer_opened, {
        variant: 'session-expired',
        reason,
        session_state: sessionStateRef.current,
        logto_is_authenticated: logtoIsAuthenticatedRef.current,
        logto_is_loading: logtoIsLoadingRef.current,
        logto_error: logtoErrorRef.current?.name ?? null,
        was_authenticated: wasAuthenticatedRef.current,
        // `path` is supplied by useAmplitudeTracking.getBasePayload() for every
        // event, so adding `pathname` here would create two route fields with
        // the same value.
      });
      logger.warn('[drawer-open] session-expired', {
        reason,
        sessionState: sessionStateRef.current,
        logtoIsAuthenticated: logtoIsAuthenticatedRef.current,
        logtoIsLoading: logtoIsLoadingRef.current,
        logtoError: logtoErrorRef.current?.message ?? null,
        wasAuthenticated: wasAuthenticatedRef.current,
        pathname: globalThis.location.pathname,
      });
      // Capture redirect intent at the moment the drawer opens, before the close
      // handler navigates the user to /home. Without this, "Sign in again" from
      // the boot-time, watchdog, or AUTHENTICATED→SESSION_EXPIRED paths would
      // drop the user's original protected route because signIn()'s internal
      // storeRedirectPath call sees /home (blocklisted) by then.
      // `shouldStoreRedirectPath` skips /home and other auth paths itself, so
      // this is a no-op for users already sitting on non-capturable routes.
      storeRedirectPath(
        globalThis.location.pathname,
        globalThis.location.search,
        globalThis.location.hash,
      );
    },
    [commitDrawerState, trackEvent],
  );

  /**
   * Open the drawer in 'service-unavailable' mode for transient backend / Logto
   * outages where the session is likely still valid. Does NOT clear auth state
   * or capture redirect intent — the user reloads or dismisses and continues.
   *
   * Logs the same `[drawer-open]` schema as `openSessionExpiredDrawer` so the
   * `reason` field is filterable across all open paths. The trigger error is
   * appended as an extra field rather than replacing the snapshot — the
   * snapshot is the cross-call contract; `triggerError` is variant-specific.
   * Like the session-expired log, this is dev-only (`logger.warn` is gated).
   */
  const openServiceUnavailableDrawer = useCallback(
    (error: unknown) => {
      // Idempotency: the proactive-refresh breaker can re-trip on every
      // failed tick within the 30s window — `recordTokenRefreshFailure()`
      // keeps incrementing past `TOKEN_FAILURE_THRESHOLD`, so each
      // subsequent failure returns `isTerminal: true` and re-fires
      // `onServiceUnavailable`. Without this guard, every re-fire emits
      // another `auth_drawer_opened` event for what is conceptually a
      // single outage, polluting the false-positive triage signal — the
      // dashboard would count retry cadence instead of distinct outages.
      // Sync write closes the StrictMode double-effect window. The close
      // handler resets the breaker on dismissal, so a new outage cycle
      // (3 fresh failures) is needed before this helper fires again.
      if (sessionDrawerStateRef.current === 'service-unavailable') {
        logger.debug('[drawer-open] suppressed duplicate service-unavailable open');
        return;
      }
      commitDrawerState('service-unavailable');

      trackEvent(ANALYTICS_EVENTS.auth_drawer_opened, {
        variant: 'service-unavailable',
        reason: 'service-unavailable',
        session_state: sessionStateRef.current,
        logto_is_authenticated: logtoIsAuthenticatedRef.current,
        logto_is_loading: logtoIsLoadingRef.current,
        logto_error: logtoErrorRef.current?.name ?? null,
        was_authenticated: wasAuthenticatedRef.current,
        // `path` is supplied by useAmplitudeTracking.getBasePayload() for every
        // event, so adding `pathname` here would create two route fields with
        // the same value.
        error_name: error instanceof Error ? error.name : null,
      });
      logger.warn('[drawer-open] service-unavailable', {
        reason: 'service-unavailable',
        sessionState: sessionStateRef.current,
        logtoIsAuthenticated: logtoIsAuthenticatedRef.current,
        logtoIsLoading: logtoIsLoadingRef.current,
        logtoError: logtoErrorRef.current?.message ?? null,
        wasAuthenticated: wasAuthenticatedRef.current,
        pathname: globalThis.location.pathname,
        triggerError: error,
      });
    },
    [commitDrawerState, trackEvent],
  );

  // Stable wrappers so each sub-hook reports its own caller reason without
  // changing their public signature. Wrapped callbacks keep `useCallback([],)`
  // semantics: identity is stable across renders so they never re-arm timers.
  const onTerminalRecovery = useCallback(() => {
    openSessionExpiredDrawer('invalid-grant-terminal');
  }, [openSessionExpiredDrawer]);
  const onWatchdogTimeout = useCallback(() => {
    openSessionExpiredDrawer('sdk-watchdog');
  }, [openSessionExpiredDrawer]);

  const { handleInvalidGrant } = useInvalidGrantHandler({
    sessionStateRef,
    dispatch,
    clearAllTokens,
    logtoSignIn,
    logtoIsAuthenticatedRef,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    onSessionExpired: onTerminalRecovery,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // SDK Loading Watchdog
  //
  // The Logto SDK can enter an internal retry loop on `/oidc/token` when the
  // stored refresh token is invalid at app boot. In that state
  // `logtoIsLoading` stays true indefinitely and the session-sync effect below
  // early-returns on it, so the user stares at the loading screen while the
  // network tab fills with 400s. Time-bound the loading state as an escape
  // hatch.
  // ─────────────────────────────────────────────────────────────────────────────
  useSdkLoadingWatchdog({
    logtoIsLoading,
    wasAuthenticated,
    sessionStateRef,
    dispatch,
    clearCachedAuth,
    openSessionExpiredDrawer: onWatchdogTimeout,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    clearAllTokens,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Session State Sync Effect
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (logtoIsLoading) {
      return;
    }

    if (logtoIsAuthenticated) {
      if (
        sessionState === AuthSessionState.INITIALIZING ||
        sessionState === AuthSessionState.UNAUTHENTICATED ||
        sessionState === AuthSessionState.SESSION_EXPIRED ||
        sessionState === AuthSessionState.REAUTHENTICATING
      ) {
        setGlobalSessionExpired(false);
        // Clear any in-flight recovery record now that auth succeeded so a
        // future invalid_grant starts a fresh attempt counter.
        clearRecoveryRecord();
        // Clear any stale drawer intent. The render-time projection
        // (`effectiveDrawerState`) hides the drawer on AUTHENTICATED, but a
        // lingering `'session-expired'` intent would re-render the drawer
        // if `sessionState` later transitions away from AUTHENTICATED for
        // an unrelated reason (e.g., a clean sign-out). Reset the intent
        // here so visibility AND stored state are consistent.
        commitDrawerState('closed');
        sessionStateRef.current = AuthSessionState.AUTHENTICATED;
        dispatch({ type: 'SET_AUTHENTICATED' });

        const wasUserInitiated = userInitiatedSignInSlot.read() === true;
        userInitiatedSignInSlot.clear();
        CrossTabAuthSync.getInstance().authStateChanged(true, {
          isUserInitiated: wasUserInitiated,
        });

        if (wasUserInitiated) {
          logger.debug(
            'Session state -> authenticated (user-initiated, broadcasting to other tabs)',
          );
        } else {
          logger.debug('Session state -> authenticated (tab loaded with existing tokens)');
        }
      }
    } else {
      // Runtime AUTHENTICATED → !logtoIsAuthenticated handling lives in the
      // tolerance-window effect below — see its comment block for the debounce
      // rationale and the transient-blip false positives it guards against.
      if (sessionState === AuthSessionState.INITIALIZING) {
        if (wasAuthenticated) {
          // Cross-tab sign-out handoff: if another tab triggered this reload,
          // SignedOutByOtherTabDrawer is going to claim the UI. We still
          // need to clear cached auth (so guest mode is consistent), but we
          // must NOT open SessionExpiredDrawer — both drawers opening at
          // once stacks them and the user only sees this one on top.
          // `signedOutByOtherTabAtMount` was captured at render; the slot is already cleared.
          if (signedOutByOtherTabAtMount) {
            logger.info(
              'Cached session invalid on init due to cross-tab sign-out - deferring UI to SignedOutByOtherTabDrawer',
            );
            sessionStateRef.current = AuthSessionState.UNAUTHENTICATED;
            dispatch({ type: 'SET_UNAUTHENTICATED' });
            clearCachedAuth();
            void clearAuthState({
              queryClient,
              setAuthenticated,
              setCurrentUser,
              clearUser,
              clearAllTokens,
            }).catch((err: unknown) => {
              logger.error(
                '[auth-clear-fail] clearAuthState after cross-tab sign-out reload threw:',
                err,
              );
            });
            return;
          }

          // Logto SDK can swallow `invalid_grant` during
          // init (corrupt/expired refresh token, Logto outage while validating)
          // and report isAuthenticated=false without propagating an error that
          // the `handleInvalidGrant` catch block could see. A user who had a
          // valid session last page load now shows up here as "unauthenticated
          // after init" even though they didn't explicitly sign out. Treat that
          // as a session-expired event and open the drawer so the user knows
          // they were signed out and can re-authenticate in place.
          logger.info('Cached session invalid on init - treating as SESSION_EXPIRED');
          setGlobalSessionExpired(true);
          sessionStateRef.current = AuthSessionState.SESSION_EXPIRED;
          dispatch({ type: 'SET_SESSION_EXPIRED' });
          // `clearCachedAuth` flips `wasAuthenticated` so the watchdog
          // self-disarms. Read `cachedUserInfo` for the analytics probe BEFORE
          // it gets flipped (the `cachedUserInfo` local here is a render-time
          // snapshot so it would work either order, but keep the read first
          // for clarity).
          trackEvent(ANALYTICS_EVENTS.auth_cached_session_invalid, {
            had_cached_user_info: !!cachedUserInfoRef.current,
          });
          clearCachedAuth();
          // Full cleanup: cancel in-flight queries (a late response cannot
          // re-hydrate a cleared cache), clear user, flip Zustand, clear
          // Logto tokens. `clearCachedAuth` alone leaves `user`,
          // `isAuthenticated`, and the `currentUserKey()` query intact,
          // which would leak stale authenticated state into guest mode
          // after the drawer closes. Fire-and-forget so the drawer renders
          // synchronously; token-clearing failures are swallowed inside
          // `clearAuthState` and surface only via `logger.error`.
          void clearAuthState({
            queryClient,
            setAuthenticated,
            setCurrentUser,
            clearUser,
            clearAllTokens,
          }).catch((err: unknown) => {
            logger.error(
              '[auth-clear-fail] clearAuthState after boot-time expired detection threw:',
              err,
            );
          });
          openSessionExpiredDrawer('boot-cached-invalid');
        } else {
          // First-time visitor or cleanly signed out previously — regular guest.
          sessionStateRef.current = AuthSessionState.UNAUTHENTICATED;
          dispatch({ type: 'SET_UNAUTHENTICATED' });
          logger.debug('Session state -> unauthenticated (guest mode)');
        }
      }
    }
  }, [
    dispatch,
    logtoIsAuthenticated,
    logtoIsLoading,
    sessionState,
    wasAuthenticated,
    signedOutByOtherTabAtMount,
    clearCachedAuth,
    commitDrawerState,
    openSessionExpiredDrawer,
    trackEvent,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    clearAllTokens,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Unexpected Loss of Authentication — TOLERANCE WINDOW
  //
  // Reaction to `AUTHENTICATED → !logtoIsAuthenticated` is debounced. The
  // Logto SDK can briefly report `isAuthenticated=false` during a refresh
  // round-trip, a cross-tab BroadcastChannel event, or a WebView focus
  // transition. The previous implementation reacted instantly: it called
  // `clearAuthState()` (which clears tokens) and opened the drawer. That
  // converted every transient blip into a real sign-out.
  //
  // Now: we set up a timer. If `logtoIsAuthenticated` flips back to true
  // within the tolerance window the dep-change triggers cleanup and cancels
  // the timer (no recovery, no drawer). If it stays false past the window
  // we re-check via `shouldFireRecovery()` and only then commit recovery.
  //
  // Self-disarm via `wasAuthenticated`: user-initiated sign-out flips this
  // flag to false via `clearCachedAuth()`, which `useSignInOut.executeSignOut`
  // calls synchronously BEFORE any await. By the time `logtoIsAuthenticated`
  // flips (after `logtoSignOut` resolves), `wasAuthenticated` is already
  // false and the early-return below short-circuits the effect — no timer
  // armed, no `auth_drawer_opened` telemetry leak. Same convention as
  // `useSdkLoadingWatchdog` (see its line 93 + module docstring).
  //
  // Action callbacks (`clearCachedAuth`, `clearAllTokens`, etc.) are NOT in
  // the dep array — only the auth state machine inputs are. Including
  // unstable callbacks here would re-arm the timer on every AuthProvider
  // re-render, effectively disabling the recovery during active sessions
  // where Zustand/React Query mutations cascade into renders. Actions are
  // accessed via a single ref that's updated each render to capture the
  // latest references; we read from it inside the timer callback so the
  // committed cleanup sees current implementations.
  // ─────────────────────────────────────────────────────────────────────────────
  const recoveryActionsRef = useRef({
    clearCachedAuth,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    clearAllTokens,
    openSessionExpiredDrawer,
  });
  recoveryActionsRef.current = {
    clearCachedAuth,
    queryClient,
    setAuthenticated,
    setCurrentUser,
    clearUser,
    clearAllTokens,
    openSessionExpiredDrawer,
  };

  useEffect(() => {
    if (logtoIsLoading) return;
    if (logtoIsAuthenticated) return;
    if (sessionState !== AuthSessionState.AUTHENTICATED) return;
    // User-initiated sign-out path: `clearCachedAuth()` ran synchronously
    // in `executeSignOut` before the await that flips `logtoIsAuthenticated`.
    // Without this guard, every native logout fires a false-positive
    // `auth_drawer_opened {reason: 'unexpected-loss'}` event, polluting the
    // triage dataset this telemetry exists to serve.
    if (!wasAuthenticated) return;

    logger.debug('[auth-unexpected-loss] starting tolerance window', {
      pathname: globalThis.location.pathname,
    });

    const timer = setTimeout(() => {
      // Re-check via refs so this sees the latest values, not a stale closure.
      // The dep-change cleanup would already have cancelled if state flipped,
      // so this is defense in depth against a same-tick race.
      const decision = shouldFireRecovery({
        logtoIsAuthenticated: logtoIsAuthenticatedRef.current,
        logtoIsLoading: logtoIsLoadingRef.current,
      });

      if (!decision) {
        logger.info('[auth-unexpected-loss] tolerance check vetoed recovery', {
          logtoIsAuthenticated: logtoIsAuthenticatedRef.current,
          logtoIsLoading: logtoIsLoadingRef.current,
          logtoError: logtoErrorRef.current?.message ?? null,
        });
        return;
      }

      logger.warn('[auth-unexpected-loss] tolerance window expired, firing recovery');
      const actions = recoveryActionsRef.current;
      setGlobalSessionExpired(true);
      sessionStateRef.current = AuthSessionState.SESSION_EXPIRED;
      dispatch({ type: 'SET_SESSION_EXPIRED' });
      // `clearCachedAuth` flips `wasAuthenticated` so the watchdog can't
      // re-arm against this freshly-cleared session.
      actions.clearCachedAuth();
      void clearAuthState({
        queryClient: actions.queryClient,
        setAuthenticated: actions.setAuthenticated,
        setCurrentUser: actions.setCurrentUser,
        clearUser: actions.clearUser,
        clearAllTokens: actions.clearAllTokens,
      }).catch((err: unknown) => {
        logger.error('[auth-clear-fail] clearAuthState after unexpected auth loss threw:', err);
      });
      actions.openSessionExpiredDrawer('unexpected-loss');
    }, UNEXPECTED_LOSS_TOLERANCE_MS);

    return () => {
      clearTimeout(timer);
    };
    // Only the auth state machine inputs are deps. Action callbacks read
    // from `recoveryActionsRef.current` at fire time so unstable references
    // don't re-arm the timer on every render. `wasAuthenticated` is a dep
    // because the self-disarm guard above depends on it as a value, not a ref.
  }, [logtoIsLoading, logtoIsAuthenticated, sessionState, wasAuthenticated]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Drawer Visibility — DERIVED, not an effect
  //
  // The auto-close behavior (close the session-expired drawer if sessionState
  // recovered to AUTHENTICATED) is computed in render, NOT via a useEffect
  // that flips state. Per CLAUDE.md "Only use useEffect for external system
  // synchronization" — flipping React state from a useEffect that watches
  // other React state is the anti-pattern this rule prevents. Deriving here
  // also avoids the extra render cycle (state change → effect fires →
  // state change → re-render) the effect approach would cause.
  //
  // `sessionDrawerState` remains the imperative "intent" — what the auth
  // paths last requested. `effectiveDrawerState` is the projection of that
  // intent against the live state machine: a stale `'session-expired'`
  // intent reads as `'closed'` once the user is authenticated again.
  // Service-unavailable is unaffected: it's shown while the user IS
  // authenticated, so AUTHENTICATED is expected for it.
  // ─────────────────────────────────────────────────────────────────────────────
  const effectiveDrawerState: SessionDrawerState =
    sessionDrawerState === 'session-expired' && sessionState === AuthSessionState.AUTHENTICATED
      ? 'closed'
      : sessionDrawerState;
  // Feed exactly what renders: the drawer's `open` is `effectiveDrawerState !== 'closed'`
  // (below), so the overlay mutex parks and unparks with what is on screen by construction.
  // On the re-authentication path the intent is reset in the same effect that dispatches
  // SET_AUTHENTICATED (one batched render), so the two agree there; the projection covers any
  // path that authenticates without resetting the intent.
  useTakeoverFeed('session', effectiveDrawerState !== 'closed');

  // ─────────────────────────────────────────────────────────────────────────────
  // Token Getter for API Client
  // ─────────────────────────────────────────────────────────────────────────────
  const getTokenForApi = useCallback(async (): Promise<string | null> => {
    if (sessionCheckers.isSessionBlocked()) {
      return null;
    }

    if (!logtoIsAuthenticatedRef.current) {
      return null;
    }

    if (tokenRequestRef.current) {
      return tokenRequestRef.current;
    }

    const tokenPromise = (async (): Promise<string | null> => {
      if (sessionCheckers.isSessionBlocked()) {
        return null;
      }

      try {
        const token = await getAccessTokenWithCrossTabLock(primaryApiResource);
        if (token) {
          // Successful fetch closes the circuit-breaker window.
          resetTokenRefreshFailures();
        }
        return token || null;
      } catch (error) {
        if (isInvalidGrantError(error)) {
          logger.info('invalid_grant detected - triggering handleInvalidGrant');
          await handleInvalidGrant(error);
          return null;
        }

        if (isNotAuthenticatedError(error)) {
          // Reaching this branch means we previously
          // WERE authenticated (top-of-function guard returns early otherwise)
          // but the SDK now throws "Not authenticated". Logto wraps the
          // underlying invalid_grant as this generic shape, so we have to
          // treat it as a failure for circuit-breaker purposes. Without this,
          // polling hooks re-request tokens forever, each getting null, each
          // hitting the backend as 401.
          const failure = recordTokenRefreshFailure();
          logger.debug(
            `Token fetch returned "Not authenticated" (${failure.count} consecutive):`,
            error,
          );
          if (failure.isTerminal) {
            logger.warn(
              'Circuit breaker tripped on "Not authenticated" - escalating to handleInvalidGrant',
            );
            await handleInvalidGrant(error);
          }
          return null;
        }

        // Persistent non-invalid_grant
        // token failures (503, network, DevTools-blocked) would otherwise
        // spin forever. Circuit-break after TOKEN_FAILURE_THRESHOLD consecutive
        // failures. The failure is still RECORDED (shared counter with
        // useProactiveTokenRefresh), but escalation is delegated: this path
        // never opens the session-expired drawer for a non-grant error — doing
        // so mislabels a backend outage as an auth failure.
        // useProactiveTokenRefresh's next scheduled/visibility tick will hit
        // the same breaker and surface the Service Unavailable drawer via
        // onServiceUnavailable. Trade-off: if proactive refresh is idle the
        // user may wait up to ~5 min for visible feedback; acceptable because
        // their session is likely still valid and unrelated to the outage.
        const failure = recordTokenRefreshFailure();
        logger.warn(`Token fetch failed (${failure.count} consecutive):`, error);
        if (failure.isTerminal) {
          logger.warn(
            'Token-refresh circuit breaker tripped on non-grant error; deferring UI to proactive refresh',
          );
        }
        return null;
      }
    })();

    tokenRequestRef.current = tokenPromise;

    try {
      return await tokenPromise;
    } finally {
      tokenRequestRef.current = null;
    }
  }, [getAccessTokenWithCrossTabLock, handleInvalidGrant, primaryApiResource, sessionCheckers]);

  // Register token getter with API client
  useEffect(() => {
    setGlobalAuthTokenGetter(getTokenForApi);
    return () => {
      setGlobalAuthTokenGetter(() => Promise.resolve(null));
    };
  }, [getTokenForApi]);

  // ─────────────────────────────────────────────────────────────────────────────
  // Proactive Token Refresh
  // ─────────────────────────────────────────────────────────────────────────────
  useProactiveTokenRefresh({
    sessionState,
    getAccessTokenWithLock: getAccessTokenWithCrossTabLock,
    apiResource: primaryApiResource,
    onInvalidGrant: handleInvalidGrant,
    onServiceUnavailable: openServiceUnavailableDrawer,
    sessionCheckers,
    lastTokenRef,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Auth Navigation
  // ─────────────────────────────────────────────────────────────────────────────
  useAuthNavigation({
    logtoIsAuthenticated,
    logtoIsLoading,
    userLoading,
    sessionState,
    logtoSignIn,
    navigate,
    trackEvent,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Auth Analytics
  // ─────────────────────────────────────────────────────────────────────────────
  useAuthAnalytics({
    user: user ?? null,
    logtoIsAuthenticated,
    userLoading,
    setCurrentUser,
    setUserProperties,
    setUserId,
    trackSessionStarted,
    checkRetentionMilestones,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // OneSignal Identity Sync
  // ─────────────────────────────────────────────────────────────────────────────
  // Keep OneSignal's external_id in lockstep with the signed-in user (external_id
  // = user.id). Dormant no-op unless VITE_ONESIGNAL_APP_ID is set. The id is
  // extracted to a named variable per the repo rule against optional chaining in
  // dependency arrays (it confuses the React Compiler and exhaustive-deps lint —
  // not a stability issue: user?.id is already a primitive).
  const oneSignalUserId = user?.id ?? null;
  useOneSignalIdentity(oneSignalUserId);

  // ─────────────────────────────────────────────────────────────────────────────
  // Sign In/Out Actions
  // ─────────────────────────────────────────────────────────────────────────────
  // Identity-stable, and load-bearing rather than tidiness. This is a dependency of useSignInOut's
  // `signOut`, which is in turn a dependency of the AuthContext value below, so as an inline arrow
  // it minted a new `signOut` and therefore a NEW CONTEXT VALUE on every AuthProvider render, which
  // re-renders every useAuth() consumer in the app past its memo() boundary.
  //
  // Navigation used to be the loudest trigger: AuthProvider called `useNavigate()`, which reads
  // RouteContext, so a bare `?tab=` replace broadcast a full re-render to every card, row and button
  // that reads auth - measured as all 20 event-feed QuestionCards still re-rendering on the
  // going-row tap after the cards themselves had stopped touching the router. `useStableNavigate`
  // above closed that path, which makes this the second line of defence rather than the fix. It
  // stays because the trigger it guards against was never only navigation: every other AuthProvider
  // render reaches the same consumers by the same route, and there are 82 non-test files calling
  // useAuth(). The setter is a useState setter, so `[]` is exact.
  const openDraftDialog = useCallback(() => {
    setIsDraftDialogOpen(true);
  }, []);

  const { signIn, signOut, executeSignOut, refreshUser } = useSignInOut({
    logtoIsAuthenticated,
    logtoSignIn,
    navigate,
    logtoSignOut,
    queryClient,
    trackEvent,
    clearCachedAuth,
    clearUser,
    setAuthenticated,
    setCurrentUser,
    setError,
    refetchUser,
    openDraftDialog,
    pendingSignOutPromiseRef,
    signOutResolverRef,
    signOutRejectRef,
    dialogCloseIntentRef,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // Draft Dialog Handlers
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCancelSignOut = useCallback(() => {
    dialogCloseIntentRef.current = 'cancel';
    setIsDraftDialogOpen(false);
    signOutRejectRef.current?.(new LogoutCancelledError());
  }, []);

  const handleConfirmSignOut = useCallback(async () => {
    dialogCloseIntentRef.current = 'confirm';
    setIsDraftDialogOpen(false);

    try {
      await executeSignOut();
      signOutResolverRef.current?.();
    } catch (error) {
      signOutRejectRef.current?.(error);
    }
  }, [executeSignOut]);

  const handleDraftDialogChange = useCallback(
    (open: boolean) => {
      if (open) {
        dialogCloseIntentRef.current = null;
        return;
      }

      if (dialogCloseIntentRef.current === 'confirm') {
        dialogCloseIntentRef.current = null;
        return;
      }

      if (dialogCloseIntentRef.current === 'cancel') {
        dialogCloseIntentRef.current = null;
        return;
      }

      handleCancelSignOut();
    },
    [handleCancelSignOut],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Session Expired Drawer Handlers
  //
  // Cleanup is centralized in handleSessionExpiredDrawerOpenChange so that
  // ALL close paths (CTAs, swipe, backdrop tap, Esc) restore guest mode.
  // Without this, dismissing via swipe/backdrop left globalSessionExpired=true
  // (rejecting every API call in the request interceptor) and sessionState=
  // SESSION_EXPIRED (suppressing useAuthNavigation's protected-route guard),
  // stranding the user on a blocked screen with no recovery UI.
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSessionExpiredDrawerOpenChange = useCallback(
    (open: boolean) => {
      // Vaul fires onOpenChange both ways. We only manage the close path
      // here — opens are driven explicitly by the open* helpers above.
      if (open) return;
      // Read the current variant from the ref, NOT from the `sessionDrawerState`
      // closure value. This is the `rerender-defer-reads` pattern: the handler
      // never renders anything (it only fires when vaul invokes `onOpenChange`),
      // so subscribing to `sessionDrawerState` would just churn this callback's
      // identity on every drawer state change, force-recreating the prop
      // passed to `<SessionExpiredDrawer>`. The funnel + cross-render effect
      // keep the ref at least as fresh as the latest committed render, so
      // reading from it is correct AND lets the handler keep a stable identity.
      const currentDrawerState = sessionDrawerStateRef.current;
      if (currentDrawerState === 'closed') return;

      // Variant-specific close behavior. Exhaustive switch + `assertNever`
      // so adding a future `SessionDrawerState` variant produces a compile-
      // time error here instead of silently falling through to the
      // unconditional `commitDrawerState('closed')` below. Mirrors the same
      // pattern used by `drawerVariantFor` at the top of this file.
      switch (currentDrawerState) {
        case 'session-expired':
          // Drop the user into guest mode — the session is gone and the
          // recovery cycle is over. Bump off protected routes synchronously
          // so useAuthNavigation does not fire a competing logtoSignIn() once
          // sessionState flips to UNAUTHENTICATED. Public routes keep the
          // user where they were.
          transitionToGuestSession({ sessionStateRef, dispatch });
          if (!isPublicRoute(globalThis.location.pathname)) {
            navigate('/home', { replace: true });
          }
          break;
        case 'service-unavailable':
          // Do NOT clear auth or transition to guest — the user may still be
          // authenticated; the outage is server side. But reset the
          // proactive-refresh breaker so a sustained outage doesn't
          // immediately re-open the drawer the user just dismissed.
          // `tokenFailureBreaker.record()` keeps returning `isTerminal: true`
          // for every subsequent failure once the threshold is reached, which
          // would re-fire `onServiceUnavailable` and put the user back in
          // the drawer they just closed — a dismiss-reopen loop they can't
          // escape. After reset, the proactive hook needs
          // `TOKEN_FAILURE_THRESHOLD` (3) fresh failures to re-trip; the user
          // gets real grace to keep working. If the outage is real, the
          // drawer comes back on its own schedule — we honor the dismiss
          // intent without losing the signal.
          resetTokenRefreshFailures();
          break;
        default:
          // The `if (currentDrawerState === 'closed') return;` above narrows
          // the value to `'session-expired' | 'service-unavailable'` here, so
          // the two cases above are exhaustive. `assertNever` makes a future
          // variant added to the type a compile-time error in this branch.
          return assertNever(currentDrawerState);
      }
      commitDrawerState('closed');
    },
    [commitDrawerState, navigate],
  );

  const handleSessionExpiredSignIn = useCallback(() => {
    // transitionToGuestSession already ran via onOpenChange. signIn() will
    // launch the Logto redirect; the navigate('/home') above is a no-op cost
    // because the page is about to be replaced.
    signIn();
  }, [signIn]);

  const handleSessionExpiredContinueAsGuest = useCallback(() => {
    // Cleanup and conditional navigation are handled in onOpenChange so the
    // explicit CTA and the swipe/backdrop dismiss share a single code path.
    // This callback is intentionally a no-op for now; reserved for analytics
    // or future per-CTA behavior.
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // Derived State
  // ─────────────────────────────────────────────────────────────────────────────
  const isLoading = logtoIsLoading || userLoading;
  const error = logtoError?.message || userError?.message || null;
  const isSessionExpired = sessionState === AuthSessionState.SESSION_EXPIRED;
  const isFullyAuthenticated = userAuthenticated && logtoIsAuthenticated && !isSessionExpired;
  const showAuthenticatedUI = isLoading ? wasAuthenticated : isFullyAuthenticated;

  // ─────────────────────────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────────────────────────
  const memoizedValue = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: isFullyAuthenticated,
      showAuthenticatedUI,
      isLoading,
      error,
      user: user ?? null,
      cachedUserInfo,
      sessionState,
      isSessionExpired,
      hasLogtoSession: logtoIsAuthenticated,
      signedOutByOtherTabAtMount,
      signIn,
      signOut,
      getAuthToken: getTokenForApi,
      refreshUser,
    }),
    [
      cachedUserInfo,
      error,
      getTokenForApi,
      isFullyAuthenticated,
      isLoading,
      isSessionExpired,
      logtoIsAuthenticated,
      refreshUser,
      sessionState,
      showAuthenticatedUI,
      signedOutByOtherTabAtMount,
      signIn,
      signOut,
      user,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
      <Dialog open={isDraftDialogOpen} onOpenChange={handleDraftDialogChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You have an unsaved draft</DialogTitle>
            <DialogDescription>
              You have an unsaved post draft. Logging out will delete this draft. Do you want to
              continue?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={handleCancelSignOut} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleConfirmSignOut} className="w-full sm:w-auto">
              Logout anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SessionExpiredDrawer
        open={effectiveDrawerState !== 'closed'}
        onOpenChange={handleSessionExpiredDrawerOpenChange}
        variant={drawerVariantFor(effectiveDrawerState)}
        onSignIn={handleSessionExpiredSignIn}
        onContinueAsGuest={handleSessionExpiredContinueAsGuest}
      />
    </AuthContext.Provider>
  );
};

// =============================================================================
// PROTECTED ROUTE
// =============================================================================

/**
 * Higher-order component for route protection
 * AIDEV-NOTE: Uses useContext directly to avoid circular import with useAuth hook
 */
export const ProtectedRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('ProtectedRoute must be used within AuthProvider');
  }
  const { isAuthenticated, isLoading } = context;
  const location = useLocation();

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    const intendedPath = location.pathname === '/' ? '/home' : location.pathname;
    storeRedirectPath(intendedPath, location.search, location.hash);
  }, [isAuthenticated, isLoading, location.hash, location.pathname, location.search]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FunnyLoadingMessage type="CTA" showText />
        </div>
      </div>
    );
  }

  return isAuthenticated ? <>{children}</> : null;
};
